import { FillButton, FillPreview, Tooltip } from '@wix/design-system';
import { RevertSmall } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { RicosContext, ZIndexContext } from './../../../ricos-context';

import classes from './color-swatches-scss';
import {
  ADD_CUSTOM_COLOR_BUTTON,
  RESET_COLOR_BUTTON,
  SWATCH,
  SWATCH_TOOLTIP,
  SWATCHES,
} from './data-hooks';

export const ColorSwatches: React.FC<{
  userColors: string[];
  selectedColor: string | undefined;
  onAddClick: () => void;
  onChange: (color: string | undefined) => void;
  size?: 'small' | 'medium';
  allowEmpty?: boolean;
  showResetButton?: boolean;
}> = ({
  userColors,
  selectedColor,
  onAddClick,
  onChange,
  size = 'small',
  allowEmpty,
  showResetButton,
}) => {
  const { t } = useContext(RicosContext);
  const zIndexService = useContext(ZIndexContext);

  return (
    <div
      className={classNames(classes.container, {
        [classes.medium]: size === 'medium',
      })}
      data-hook={SWATCHES}
    >
      {showResetButton && (
        <FillButton
          icon={<RevertSmall />}
          iconSize="medium"
          onClick={() => onChange(undefined)}
          dataHook={RESET_COLOR_BUTTON}
          tooltipContent={t('ColorPickerResetColor_Tooltip')}
          tooltipProps={{
            appendTo: el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal')),
            zIndex: zIndexService.getZIndex('TOOLTIP'),
          }}
        />
      )}
      <FillButton
        iconSize="medium"
        onClick={onAddClick}
        dataHook={ADD_CUSTOM_COLOR_BUTTON}
        tooltipContent={t('ColorPickerAddCustomColor_Tooltip')}
        tooltipProps={{
          appendTo: el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal')),
          zIndex: zIndexService.getZIndex('TOOLTIP'),
        }}
      />
      {allowEmpty && <EmptyColorSwatch onClick={onChange} selected={selectedColor === undefined} />}
      {userColors.map(userColor => (
        <ColorSwatch
          key={userColor}
          fill={userColor}
          selectedColor={selectedColor}
          onClick={onChange}
        />
      ))}
    </div>
  );
};

const ColorSwatch: React.FC<{
  fill: string;
  selectedColor: string | undefined;
  onClick: (color: string) => void;
}> = ({ fill, selectedColor, onClick }) => {
  return (
    <Tooltip
      appendTo={el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal'))}
      content={fill}
      dataHook={SWATCH_TOOLTIP}
    >
      <FillPreview
        className={classes.colorSwatch}
        selected={selectedColor === fill}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClick(fill);
        }}
        fill={fill}
        dataHook={SWATCH}
      />
    </Tooltip>
  );
};

const EmptyColorSwatch: React.FC<{ onClick: (color: undefined) => void; selected: boolean }> = ({
  onClick,
  selected,
}) => {
  const { t } = useContext(RicosContext);
  return (
    <Tooltip
      appendTo={el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal'))}
      content={t('ColorPicker_RemoveColor_Tooltip')}
      dataHook={SWATCH_TOOLTIP}
    >
      <FillPreview
        className={classes.colorSwatch}
        onClick={() => onClick(undefined)}
        selected={selected}
        dataHook={SWATCH}
      />
    </Tooltip>
  );
};
